import React from 'react';
import {NavLink, useNavigate, useParams} from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import IconApprove from '@mui/icons-material/CheckBox';
import IconDraft from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import PublishAccessIcon from '@mui/icons-material/Language';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/SecurityUpdate';
import TranslateIcon from '@mui/icons-material/Translate';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Breadcrumbs,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {AxiosError, AxiosResponse} from 'axios';

import {
  apiCall,
  areKeysStatusesSame,
  areRevisionsSame,
  getRole,
  getXliffUrl,
  hash53,
  hideLoader,
  showLoader,
} from '../../utils';

import {ActiveKeyDialog} from './ActiveKeyDialog';
import {CommitDialog} from './CommitDialog';
import {ErrorDialog} from './ErrorDialog';
import {ImportKeysDialog} from './ImportKeysDialog';
import {KeyStatusSelector} from './KeyStatus';
import {KeyTranslationRow} from './KeyTranslationRow';
import {NewLangDialog} from './NewLangDialog';
import {PublishAccessDialog} from './PublishAccessDialog';
import {PullDialog} from './PullDialog';
import {ReInitValidationDialog} from './ReInitValidationDialog';
import {TranslationProgressDialog} from './TranslationProgressDialog';

import {
  CommitRequestDTO,
  ExpandedKeys,
  KeyTranslation,
  ProjectKeyStatus,
  ProjectKeyStatuses,
  ProjectRevision,
  ProjectRevisionSaveDTO,
  ProjectTranslation,
  ProjectWithRevisionDTO,
  TranslationRequestDTO,
  TranslationResponseDTO,
  TranslationSource,
} from '@bumpy/i18n_common';

type DialogType =
  | 'NONE'
  | 'NEW_LANG'
  | 'PULL'
  | 'COMMIT'
  | 'TRANSLATION_PROGRESS'
  | 'IMPORT_KEYS'
  | 'PUBLISH_ACCESS'
  | 'REINIT_VALIDATION';

type SearchRef = {
  search: string;
  lang: string;
  keysIndexes?: number[];
};

export const ProjectRevisionForm: React.FC = () => {
  const role = getRole();
  const navigate = useNavigate();
  const {projectUuid, revisionUuid} = useParams();

  const [dto, setDto] = React.useState<ProjectWithRevisionDTO | undefined>(
    undefined,
  );
  const originalDto = React.useRef<ProjectWithRevisionDTO>();
  const translationRunning = React.useRef(false);
  const [translationMessage, setTranslationMessage] = React.useState('');
  const [lang, setLang] = React.useState('en');
  const [search, setSearch] = React.useState('');
  const [keyStatusFilter, setKeyStatusFilter] =
    React.useState<ProjectKeyStatus>('AllStatuses');
  const searchRef = React.useRef<SearchRef>({search: '', lang: ''});
  const [error, setError] = React.useState<string | undefined>();
  const [activeKey, setActiveKey] = React.useState<string | undefined>();
  const [dialogType, setDialogType] = React.useState<DialogType>('NONE');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [checkLang, setCheckLang] = React.useState('uk');
  const [expandedKeysOnly, setExpandedKeysOnly] = React.useState(false);
  const [autoSave, setAutoSave] = React.useState(true);
  const [runValidation, setRunValidation] = React.useState(true);
  const [newName, setNewName] = React.useState<string>();
  const focusedKey = React.useRef<string | undefined>(undefined);

  let translationIndex = 0;
  if (
    dto &&
    dto.revision.translationIndex &&
    dto.revision.translationIndex[lang]
  ) {
    translationIndex = dto.revision.translationIndex[lang];
  }

  function refresh(): void {
    apiCall({
      method: 'GET',
      url: `/projects/${projectUuid}/revisions/${revisionUuid}`,
      secure: true,
    }).then((response) => {
      const dto = response.data as ProjectWithRevisionDTO;
      setDto(dto);
      setLang(dto.project.mainLang);
      originalDto.current = JSON.parse(JSON.stringify(dto));
    });
  }

  React.useEffect(refresh, [projectUuid, revisionUuid]);

  function onSaveClick(): Promise<void> {
    showLoader('Saving...');
    let url = `/projects/${projectUuid}/revisions/${revisionUuid}`;
    if (!runValidation) {
      url += '?auto_save=1';
    }
    const data: ProjectRevisionSaveDTO = {
      keyStatuses: dto!.keysStatuses,
      updatedRevision: dto!.revision,
    };
    return apiCall({
      method: 'PUT',
      url,
      data,
      secure: true,
    })
      .then((response) => {
        const revision = response.data.revision as ProjectRevision;
        if (revision.uuid === dto!.revision.uuid) {
          const newDto = {...dto!};
          newDto.revision = revision;
          originalDto.current = JSON.parse(JSON.stringify(newDto));
          setDto(newDto);
        } else {
          alert('Revision has conflict, created new one');
          navigate(`/projects/${projectUuid}/revisions/${revision.uuid}`);
        }
      })
      .catch((e) => {
        if (e instanceof AxiosError && e.response) {
          const {data} = e.response;
          if (data.message) {
            setError(data.message);
            return;
          }
        }
        alert('Unknown error during saving');
      })
      .finally(hideLoader);
  }

  function setKeyStatusForLang(
    lang: string,
    key: string,
    status: ProjectKeyStatus,
  ): void {
    setDto({
      ...dto!,
      keysStatuses: {
        ...dto!.keysStatuses,
        [lang]: {
          ...dto!.keysStatuses[lang],
          [key]: {
            text: dto!.revision.translations[lang][key],
            main_text_hash: hash53(
              dto!.revision.translations[dto!.project.mainLang][key],
            ),
            status,
          },
        },
      },
    });
  }

  function setKeyStatus(key: string, status: ProjectKeyStatus): void {
    setKeyStatusForLang(lang, key, status);
  }

  function setTranslation(key: string, translation: string): void {
    const newDto = {...dto!};
    newDto.revision.translations[lang][key] = translation;
    setDto(newDto);
  }

  function setKeyTranslations(
    key: string,
    translations: KeyTranslation,
    statuses: ProjectKeyStatuses,
  ): boolean {
    const newDto = {...dto!};
    const langs = Object.keys(translations);
    for (const lang of langs) {
      newDto.revision.translations[lang][key] = translations[lang];
      let s = newDto.keysStatuses[lang];
      if (!s) {
        newDto.keysStatuses[lang] = s = {};
      }
      s[key] = statuses[lang];
    }
    const indexActive = newDto.revision.keys.indexOf(activeKey!);
    const indexKey = newDto.revision.keys.indexOf(key);
    if (key !== activeKey) {
      if (indexKey !== -1) {
        alert('New key name exists');
        return false;
      }
      for (const lang of langs) {
        delete newDto.revision.translations[lang][activeKey!];
      }
    }
    if (indexActive === -1) {
      newDto.revision.keys.push(key);
    } else {
      newDto.revision.keys[indexActive] = key;
    }

    const {keysIndexes} = searchRef.current;
    if (keysIndexes) {
      const indexOfIndex = keysIndexes.indexOf(indexActive);
      if (indexOfIndex !== -1) {
        keysIndexes[indexOfIndex] = Object.keys(
          newDto.revision.translations[lang],
        ).indexOf(key);
      }
    }

    setDto(newDto);
    setActiveKey(undefined);
    return true;
  }

  function deleteActiveKey(): void {
    const newDto = {...dto!};
    const langs = Object.keys(newDto.revision.translations);
    for (const lang of langs) {
      delete newDto.revision.translations[lang][activeKey!];
    }
    const index = newDto.revision.keys.indexOf(activeKey!);
    newDto.revision.keys.splice(index, 1);
    setDto(newDto);
    setActiveKey(undefined);
  }

  function addNewLang(newLang: string): void {
    const newDto = {...dto!};
    const translation: ProjectTranslation = {};
    for (const key of dto!.revision.keys) {
      translation[key] = '';
    }
    newDto.revision.translations[newLang] = translation;
    setDto(newDto);
    setLang(newLang);
    setDialogType('NONE');
  }

  function pull(branch: string): void {
    showLoader('Pulling...');
    apiCall({
      method: 'POST',
      url: `/projects/${projectUuid}/revisions/${revisionUuid}/pull/${branch}`,
      secure: true,
    })
      .then((response) => {
        const revision = response.data.revision as ProjectRevision;
        const newDto = {...dto!};
        newDto.revision = revision;
        setDto(newDto);
        setDialogType('NONE');
      })
      .catch((e) => {
        if (e instanceof AxiosError && e.response) {
          alert('Error during pull "' + e.response.data.err + '"');
          return;
        }
        alert('Unknown error during pull');
      })
      .finally(hideLoader);
  }

  function commit(branch: string, message: string): void {
    const data: CommitRequestDTO = {
      branch,
      message,
    };

    showLoader('Commiting...');
    apiCall({
      method: 'POST',
      url: `/projects/${projectUuid}/revisions/${revisionUuid}/commit`,
      data,
      secure: true,
    })
      .then(() => {
        const newDto = {...dto!};
        newDto.revision.committed = true;
        setDto(newDto);
        setDialogType('NONE');
      })
      .catch((e) => {
        if (e instanceof AxiosError && e.response) {
          alert('Error during commit "' + e.response.data.err + '"');
          return;
        }
        alert('Unknown error during commit');
      })
      .finally(hideLoader);
  }

  function publish(): void {
    showLoader('Publishing...');
    apiCall({
      method: 'POST',
      url: `/projects/${projectUuid}/revisions/${revisionUuid}/publish`,
      secure: true,
    })
      .then(() => {
        const newDto = {...dto!};
        newDto.revision.published = true;
        setDto(newDto);
      })
      .catch((e) => {
        if (e instanceof AxiosError && e.response) {
          alert('Error during publish "' + e.response.data.err + '"');
          return;
        }
        alert('Unknown error during publish');
      })
      .finally(hideLoader);
  }

  function renderSaveButton(same: boolean): React.ReactNode {
    return (
      <Button
        disabled={same}
        key="save"
        variant="contained"
        color="primary"
        onClick={onSaveClick}
        startIcon={<SaveIcon />}
      >
        Save {same}
      </Button>
    );
  }

  function renderActionButton(): React.ReactNode {
    if (originalDto.current && dto) {
      const same =
        areRevisionsSame(originalDto.current.revision, dto.revision) &&
        areKeysStatusesSame(originalDto.current.keysStatuses, dto.keysStatuses);
      if (!same) {
        return (
          <>
            {renderSaveButton(same)}
            {dto.project.validationRunScript && (
              <FormControlLabel
                control={<Checkbox defaultChecked={runValidation} />}
                value={runValidation}
                label="Validate"
                onChange={(_, value) => {
                  setRunValidation(value);
                }}
              />
            )}
          </>
        );
      }

      const result: React.ReactNode[] = [];

      if (dto.project.git && same && !dto.revision.committed) {
        result.push(
          <Button
            key="commit"
            variant="contained"
            color="primary"
            onClick={() => {
              setDialogType('COMMIT');
            }}
            startIcon={<UploadIcon />}
          >
            Commit
          </Button>,
        );
      }
      if (dto.project.publishable && !dto.revision.published) {
        result.push(
          <Button
            key="publish"
            variant="contained"
            color="primary"
            onClick={publish}
            startIcon={<PublishIcon />}
          >
            Publish
          </Button>,
        );
      }

      if (result.length > 0) {
        return result;
      }
    }
  }

  let keys: string[] | undefined;
  let translation: ProjectTranslation | undefined;
  let approvedKeysCount = 0;
  let draftKeysCount = 0;
  let totalKeysCount = 1; // to avoid division by zero

  if (dto) {
    translation = dto.revision.translations[lang];
    // keys = dto.revision.keys;
    const keysOriginal = Object.keys(translation);
    totalKeysCount = keysOriginal.length;
    keys = keysOriginal;

    const keysStatuses = dto.keysStatuses[lang];
    if (keysStatuses) {
      for (const key of keysOriginal) {
        const status = keysStatuses[key];
        if (!status) {
          draftKeysCount++;
          continue;
        }
        if (status.status === 'Draft') {
          draftKeysCount++;
        } else if (
          status.status === 'Approved' &&
          status.text === translation[key]
        ) {
          approvedKeysCount++;
        }
      }
    }
    if (search.length > 0) {
      if (
        searchRef.current.search === search &&
        searchRef.current.lang === lang
      ) {
        keys = [];
        for (const keyIndex of searchRef.current.keysIndexes!) {
          keys.push(keysOriginal[keyIndex]);
        }
      } else {
        // try {
        //   const regex = new RegExp(search);
        //   keys = keysOriginal.filter((key) => {
        //     if (regex.test(key)) {
        //       return true;
        //     }
        //     const v = translation![key];
        //     return regex.test(v);
        //   });
        // } catch {
        const s = search.trim().toLowerCase();
        keys = keysOriginal.filter((key) => {
          if (key.toLowerCase().includes(s)) {
            return true;
          }
          const v = translation![key];
          return v.toLowerCase().includes(s);
        });
        // }
        const keysIndexes: number[] = [];
        for (const key of keys) {
          keysIndexes.push(keysOriginal.indexOf(key));
        }
        searchRef.current = {search, lang, keysIndexes};
      }
    }
    if (expandedKeysOnly) {
      keys = keys.filter((key) => dto.revision.expandedKeys[key]);
    }
  }

  React.useEffect(() => {
    function onKeyPress(event: KeyboardEvent): void {
      if (!event.ctrlKey) {
        return;
      }
      if (event.key === 'f') {
        event.preventDefault();
        document.getElementById('search-input')?.focus();
      } else if (event.key === 'Enter') {
        const key = focusedKey.current;
        if (key && keys) {
          event.preventDefault();
          setKeyStatusForLang(lang, key, 'Approved');
          const index = keys.indexOf(key);
          if (index !== -1 && index < keys.length - 1) {
            document.getElementById(`key_${keys[index + 1]}-input`)?.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [setKeyStatusForLang, keys]);

  if (keyStatusFilter !== 'AllStatuses' && dto && keys && dto.keysStatuses) {
    const keyStatuses = dto.keysStatuses[lang];
    if (keyStatuses) {
      const translations = dto.revision.translations[lang];
      if (keyStatusFilter === 'Draft') {
        keys = keys.filter((key) => {
          const status = keyStatuses[key];
          if (!status || status.status === 'Draft') {
            return true;
          }
          return status.text !== translations[key];
        });
      } else {
        keys = keys.filter((key) => {
          const status = keyStatuses[key];
          if (!status) {
            return false;
          }
          if (status.status !== keyStatusFilter) {
            return false;
          }
          return status.text === translations[key];
        });
      }
    } else {
      if (keyStatusFilter !== 'Draft') {
        keys = [];
      }
    }
  }

  const searchTranslation = search.length > 0 && keys!.length > 0;

  async function onTranslateClick(): Promise<void> {
    const data: TranslationRequestDTO = {
      projectUuid: projectUuid!,
      checkLang,
      fromLang: dto!.project.mainLang,
      content: {},
      toLangs: [],
    };
    let keysForTranslation: string[];
    if (searchTranslation) {
      keysForTranslation = keys!;
      data.toLangs = Object.keys(dto!.revision.translations).filter(
        (lang) => lang !== dto!.project.mainLang,
      );
    } else {
      keysForTranslation = dto!.revision.keys;
      data.toLangs.push(lang);
    }
    for (const key of keysForTranslation) {
      const content = dto!.revision.translations[dto!.project.mainLang][key];
      if (!content || content.trim().length < 1) {
        alert('Missed original text for ' + key);
        return;
      }
    }
    setDialogType('TRANSLATION_PROGRESS');
    translationRunning.current = true;
    try {
      const page = 20;
      for (
        let i = translationIndex;
        i < keysForTranslation.length && translationRunning.current;
        i += page
      ) {
        data.content = {};
        const max = Math.min(keysForTranslation.length, i + page);
        for (let j = i; j < max; ++j) {
          const key = keysForTranslation[j];
          data.content[key] =
            dto!.revision.translations[dto!.project.mainLang][key];
        }
        const progress = Math.ceil((i / keysForTranslation.length) * 100);
        setTranslationMessage(`Translating ${progress}% ...`);
        let response: AxiosResponse;
        try {
          response = await apiCall({
            method: 'POST',
            url: '/translations',
            secure: true,
            data,
          });
        } catch {
          alert('Some error during translation');
          continue;
        }
        const newExpandedKeys: ExpandedKeys = {...dto!.revision.expandedKeys};
        const newDto = {...dto!};
        const revision = {
          ...dto!.revision,
        };
        for (const translatedLang of Object.keys(response.data)) {
          const translatedKeys = (response.data as TranslationResponseDTO)[
            translatedLang
          ];
          for (const key of Object.keys(translatedKeys)) {
            const translation = translatedKeys[key];
            const sources = Object.keys(translation) as TranslationSource[];
            let matches = 1;
            let checks = 1;
            const picked = translation[sources[0]].tr[0];
            let first = translation[sources[0]].check;
            for (let j = 1; j < sources.length; ++j) {
              if (picked === translation[sources[j]].tr[0]) {
                matches++;
              }
              if (first && first === translation[sources[j]].check) {
                checks++;
              }
            }
            if (matches === sources.length) {
              newExpandedKeys[key] = false;
              newDto.revision.translations[translatedLang][key] = picked;
            } else if (checks === sources.length) {
              if (
                translation.deepl &&
                typeof translation.deepl.tr[0] === 'string'
              ) {
                first = translation.deepl.tr[0];
              }

              newExpandedKeys[key] = false;
              newDto.revision.translations[translatedLang][key] = first!;
            } else {
              newExpandedKeys[key] = true;
            }
          }
          revision.translationDto = {
            ...dto!.revision!.translationDto,
            [translatedLang]: {
              ...(dto!.revision!.translationDto
                ? dto!.revision!.translationDto[translatedLang]
                : {}),
              ...translatedKeys,
            },
          };
          if (!revision.translationIndex) {
            revision.translationIndex = {};
          }
          revision.translationIndex[translatedLang] = i;
        }
        revision.expandedKeys = newExpandedKeys;

        setDto({
          ...dto!,
          revision,
        });
        if (autoSave && i >= 5 && i % 5 === 0) {
          await apiCall({
            method: 'PUT',
            url: `/projects/${projectUuid}/revisions/${revisionUuid}?auto_save=1`,
            data: revision,
            secure: true,
          });
        }
      }
    } finally {
      setDialogType('NONE');
    }
  }

  const disabledEdit = role !== 'admin' && dto?.project.mainLang === lang;

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Breadcrumbs aria-label="breadcrumb">
          <Breadcrumbs aria-label="breadcrumb">
            <NavLink to="/">Projects</NavLink>
            <NavLink to={`/${projectUuid}`}>{dto?.project.name ?? ''}</NavLink>
            <Stack direction="row" alignItems="center">
              {newName ? (
                <TextField
                  label="New name"
                  margin="none"
                  size="small"
                  value={newName}
                  onChange={(event) => {
                    setNewName(event.target.value);
                  }}
                />
              ) : (
                <Typography color="secondary">
                  {dto?.revision.name ?? ''}
                </Typography>
              )}
              {role === 'admin' && newName && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setNewName(undefined);
                    apiCall({
                      method: 'PUT',
                      url: `/projects/${projectUuid}/revisions/${revisionUuid}/name`,
                      data: {name: newName},
                      secure: true,
                    }).then(() => {
                      setDto({
                        ...dto!,
                        revision: {
                          ...dto!.revision,
                          name: newName,
                        },
                      });
                    });
                  }}
                >
                  <SaveIcon />
                </IconButton>
              )}
              {role === 'admin' && !newName && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setNewName(dto?.revision.name);
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Stack>
          </Breadcrumbs>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={2}>
        {role !== 'admin' &&
          dto &&
          originalDto.current &&
          renderSaveButton(
            areRevisionsSame(originalDto.current.revision, dto.revision) &&
              areKeysStatusesSame(
                originalDto.current.keysStatuses,
                dto.keysStatuses,
              ),
          )}
      </Grid>
      <Grid item xs={4}>
        {dto && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <Typography fontSize={14} marginTop={1}>
              {`✅ Approved: ${approvedKeysCount}/${totalKeysCount}: `}
              <b>{`${Math.ceil(
                (approvedKeysCount / totalKeysCount) * 100,
              )}%`}</b>
            </Typography>

            <Select
              value={lang}
              variant="standard"
              onChange={(event) => {
                const {value} = event.target;
                if (value === 'new') {
                  setDialogType('NEW_LANG');
                } else {
                  setLang(value);
                }
              }}
            >
              {role === 'admin' && (
                <MenuItem value="new">
                  <AddIcon />
                  New
                </MenuItem>
              )}
              {Object.keys(dto.revision.translations).map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
      </Grid>
      {role === 'admin' && (
        <Grid item xs={12}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
          >
            {dto?.project.publishable && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setDialogType('PUBLISH_ACCESS');
                }}
                startIcon={<PublishAccessIcon />}
              >
                Publish access
              </Button>
            )}
            {dto?.project.git && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setDialogType('PULL');
                }}
                startIcon={<DownloadIcon />}
              >
                Pull
              </Button>
            )}
            {dto?.project.git && dto?.project.validationSetupProjectScript && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setDialogType('REINIT_VALIDATION');
                }}
                startIcon={<PublishedWithChangesIcon />}
              >
                Reinit validation
              </Button>
            )}
            {renderActionButton()}
          </Stack>
        </Grid>
      )}
      <Grid item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <TextField
            label="Search (Ctrl+F)"
            margin="none"
            size="small"
            id="search-input"
            sx={{width: 270}}
            value={search}
            onChange={(event) => {
              setPage(0);
              setSearch(event.target.value);
            }}
          />
          <KeyStatusSelector
            allStatuses
            value={keyStatusFilter}
            onChange={setKeyStatusFilter}
          />
          {role === 'admin' && (
            <FormControlLabel
              control={<Checkbox defaultChecked={expandedKeysOnly} />}
              value={expandedKeysOnly}
              label="Expanded only"
              onChange={(_, value) => {
                setExpandedKeysOnly(value);
              }}
            />
          )}
          <TablePagination
            rowsPerPageOptions={[50, 100, 200]}
            variant="body"
            count={keys?.length ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, page) => {
              setPage(page);
            }}
            onRowsPerPageChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              setPage(0);
              setRowsPerPage(parseInt(event.target.value, 10));
            }}
          />
        </Stack>
      </Grid>
      {dto && (
        <>
          <Grid item xs={12}>
            <TableContainer sx={{maxHeight: 700}}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={400}>
                      {role === 'admin' && (
                        <>
                          <IconButton
                            disabled={approvedKeysCount === totalKeysCount}
                            color="success"
                            onClick={() => {
                              const newDto = {...dto!};
                              let keysStatuses = newDto.keysStatuses[lang];
                              if (!keysStatuses) {
                                keysStatuses = {};
                                newDto.keysStatuses[lang] = keysStatuses;
                              }
                              for (const key of keys!) {
                                keysStatuses[key] = {
                                  text: newDto.revision.translations[lang][key],
                                  main_text_hash: hash53(
                                    newDto.revision.translations[
                                      newDto.project.mainLang
                                    ][key],
                                  ),
                                  status: 'Approved',
                                };
                              }
                              setDto(newDto);
                            }}
                          >
                            <IconApprove />
                          </IconButton>
                          <IconButton
                            disabled={draftKeysCount === totalKeysCount}
                            color="info"
                            onClick={() => {
                              const newDto = {...dto!};
                              const keysStatuses = newDto.keysStatuses[lang];
                              if (keysStatuses) {
                                for (const key of keys!) {
                                  delete keysStatuses[key];
                                }
                                setDto(newDto);
                              }
                            }}
                          >
                            <IconDraft />
                          </IconButton>
                        </>
                      )}
                      Key
                      {role === 'admin' && (
                        <>
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={() => {
                              setActiveKey('new_key');
                            }}
                            startIcon={<KeyIcon />}
                          >
                            New
                          </Button>
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={() => {
                              setDialogType('IMPORT_KEYS');
                            }}
                            startIcon={<DownloadIcon />}
                          >
                            Import
                          </Button>
                        </>
                      )}
                    </TableCell>
                    <TableCell width={400}>
                      Translation
                      {(lang !== dto.project.mainLang || searchTranslation) &&
                        role === 'admin' && (
                          <>
                            <TextField
                              value={translationIndex.toString()}
                              type="number"
                              variant="standard"
                              sx={{width: 60, marginLeft: 1}}
                              onChange={(event) => {
                                const value = Math.min(
                                  Math.max(Number(event.target.value), 0),
                                  dto.revision.keys.length - 1,
                                );

                                setDto({
                                  ...dto,
                                  revision: {
                                    ...dto!.revision,
                                    translationIndex: {
                                      ...dto!.revision!.translationIndex,
                                      [lang]: value,
                                    },
                                  },
                                });
                              }}
                            />
                            <IconButton onClick={onTranslateClick}>
                              <TranslateIcon />
                            </IconButton>
                            <Select
                              value={checkLang}
                              variant="standard"
                              onChange={(event) => {
                                setCheckLang(event.target.value);
                              }}
                            >
                              {Object.keys(dto.revision.translations).map(
                                (lang) => (
                                  <MenuItem key={lang} value={lang}>
                                    {lang}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </>
                        )}
                      {lang !== dto.project.mainLang && role === 'admin' && (
                        <a
                          href={getXliffUrl(dto, lang)}
                          download={`${dto.project.name}_${dto.revision.name}_${lang}.xliff`}
                        >
                          📝Download xliff
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderKeys(
                    dto,
                    keys!,
                    translation!,
                    page,
                    rowsPerPage,
                    lang,
                    disabledEdit,
                    focusedKey,
                    setActiveKey,
                    setDto,
                    setKeyStatus,
                    setTranslation,
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {activeKey && (
            <ActiveKeyDialog
              activeKey={activeKey}
              checkLang={checkLang}
              revision={dto.revision}
              screenshotKeysConnections={dto.screenshotsKeysConnections}
              keyStatuses={dto.keysStatuses}
              role={role}
              project={dto.project}
              onChangeActiveKey={setActiveKey}
              onClose={() => setActiveKey(undefined)}
              onDeleteKey={deleteActiveKey}
              onSave={setKeyTranslations}
            />
          )}
          <NewLangDialog
            open={dialogType === 'NEW_LANG'}
            onClose={() => setDialogType('NONE')}
            onNewLangCreate={addNewLang}
          />
          <PullDialog
            branch={dto?.revision.branch}
            open={dialogType === 'PULL'}
            onClose={() => setDialogType('NONE')}
            onPull={pull}
          />
          <CommitDialog
            branch={dto?.revision.branch}
            open={dialogType === 'COMMIT'}
            onClose={() => setDialogType('NONE')}
            onCommit={commit}
          />
          <ErrorDialog
            error={error}
            onClose={() => {
              setError(undefined);
            }}
          />
          <TranslationProgressDialog
            autoSave={autoSave}
            message={translationMessage}
            open={dialogType === 'TRANSLATION_PROGRESS'}
            onAutoSave={setAutoSave}
            onPause={() => {
              setDialogType('NONE');
              translationRunning.current = false;
            }}
          />
          <ReInitValidationDialog
            branch={dto.revision.validationBranch || dto.revision.branch}
            open={dialogType === 'REINIT_VALIDATION'}
            onReInit={(branch) => {
              showLoader('Re-init validation...');
              apiCall({
                method: 'POST',
                url: `/projects/${projectUuid}/revisions/${revisionUuid}/validation/setup`,
                data: {branch},
                secure: true,
              }).finally(() => {
                setDialogType('NONE');
                hideLoader();
                if (branch !== dto!.revision.validationBranch) {
                  dto!.revision.validationBranch = branch;
                  setDto({...dto!});
                }
              });
            }}
            onClose={() => {
              setDialogType('NONE');
            }}
          />
          <ImportKeysDialog
            open={dialogType === 'IMPORT_KEYS'}
            onClose={() => {
              setDialogType('NONE');
            }}
            onKeysImported={(translation: ProjectTranslation) => {
              const keys = Object.keys(translation);
              if (keys.length === 0) {
                alert('Nothing to import');
                return;
              }
              const currentTranslation = dto!.revision.translations[lang];
              for (const key of keys) {
                if (currentTranslation[key]) {
                  alert('Key ' + key + ' already exists');
                  return;
                }
                if (translation[key].trim().length === 0) {
                  alert('Empty string value for key ' + key);
                  return;
                }
              }
              for (const key of keys) {
                if (!dto!.revision.keys.includes(key)) {
                  dto!.revision.keys.push(key);
                }
                currentTranslation[key] = translation[key];
              }
              setDialogType('NONE');
            }}
          />

          <PublishAccessDialog
            project={dto.project}
            revision={dto.revision}
            open={dialogType === 'PUBLISH_ACCESS'}
            onClose={() => {
              setDialogType('NONE');
            }}
          />
        </>
      )}
    </Grid>
  );
};

function renderKeys(
  dto: ProjectWithRevisionDTO,
  keys: string[],
  translation: ProjectTranslation,
  page: number,
  rowsPerPage: number,
  lang: string,
  disabled: boolean,
  focusedKey: React.MutableRefObject<string | undefined>,
  setActiveKey: (key: string) => void,
  setDto: (dto: ProjectWithRevisionDTO) => void,
  setKeyStatus: (key: string, status: ProjectKeyStatus) => void,
  setTranslation: (key: string, translation: string) => void,
): React.ReactNode {
  const translationLangDto = dto.revision.translationDto
    ? dto.revision.translationDto[lang]
    : undefined;
  const quickMode =
    Object.keys(dto.revision.translations).length > 1 &&
    lang !== dto.project.mainLang;

  const start = rowsPerPage * page;
  let end = start + rowsPerPage;
  if (end > keys.length) {
    end = keys.length - 1;
  }

  const views: React.ReactNode[] = [];
  for (let i = start; i <= end; ++i) {
    const key = keys[i];

    views.push(
      <KeyTranslationRow
        disable={disabled}
        expandedKeys={dto.revision.expandedKeys}
        key={key}
        keyName={key}
        lang={lang}
        project={dto.project}
        projectKeysStatuses={dto.keysStatuses}
        focusedKey={focusedKey}
        translation={translation[key]}
        translations={dto.revision.translations}
        quickMode={quickMode}
        translationKeyDto={
          translationLangDto ? translationLangDto[key] : undefined
        }
        onKeyClick={setActiveKey}
        setExpandedKeys={(expandedKeys: ExpandedKeys) => {
          setDto({
            ...dto,
            revision: {
              ...dto.revision,
              expandedKeys,
            },
          });
        }}
        onKeyStatusChange={setKeyStatus}
        onTranslationChange={setTranslation}
      />,
    );
  }

  return views;
}
